/* eslint-disable @typescript-eslint/camelcase */
import { getStorage } from '@utils/localStorage';
import { getEnv, ENV } from '@utils/getEnv';

import { BellConstructorConfig } from './Bell';

/** 各个内置铃声和资源文件名的对应关系 */
const bellResourceMap = {
    preorder: 'preOrderSound.ogg',
    preorderAcceptedByRider: 'preOrderAcceptedByRider.ogg',
    newpreorder: 'newPreOrderSound.ogg',
    autonewpreorder: 'autoNewPreOrderSound.ogg',
    neworder: 'orderSound1.ogg',
    autoneworder: 'autoOrderSound1.ogg',
    yxsNewOrder: 'yxsNewOrder.ogg',
    other: 'otherDevice.ogg',
    preorderOther: 'preOrderOtherDevice.ogg',
    reminder: 'reminderSound1.ogg',
    cancel: 'cancelSound1.ogg',
    refund: 'refundSound1.ogg',
    refundUrge: 'refundUrge.ogg',
    exception: 'deliveryException.ogg',
    'paotui-exception': 'paotuiDeliveryException.ogg',
    'new-message': 'newMessage.ogg',
    'message-remind1': 'messageRemind1.ogg',
    autoSendDeliveryFail: 'autoSendDeliveryFail.ogg',
    'meal-slow': 'mealSlow.ogg',
    'meal-exception': 'mealException.ogg',
    mealSlow_sharkOrder: 'mealSlow_sharkOrder.ogg',
    mealSlow_riderWait: 'mealSlow_riderWait.ogg',
    mealSlow_other: 'mealSlow_other.ogg',
    mealSlow_multiSku: 'mealSlow_multiSku.ogg',
    mealSlow_bigOrder: 'mealSlow_bigOrder.ogg',
    negativeIncome: 'negativeIncome.ogg',
    breakRule: 'breakRule.ogg',
    earlyWarning: 'earlyWarning.ogg',
    orderWillTimeout: 'order_will_timeout.ogg',
    autoAcceptDisconnect: 'auto_accept_disconnect.ogg',
    dispatcherCancel: 'dispatcherCancel.ogg',
    dispatcherCancelAndRedispatch: 'dispatcherCancelAndRedispatch.ogg',
    dispatchStopped: 'dispatchStopped.ogg',
    dispatchPaused: 'dispatchPaused.ogg',
    dispatcherNotArrival: 'dispatcherNotArrival.ogg',
    dispatcherNotTakeMeal: 'dispatcherNotTakeMeal.ogg',
    dispatcherTransfer: 'dispatcherTransfer.ogg',
    newDispatcher: 'newDispatcher.ogg',
    noDispatcherAcceptOrder: 'noDispatcherAcceptOrder.ogg',
    preparationOrderNew: 'preparationOrderNew.ogg',
    preparationOrderPrepMeal: 'preparationOrderPrepMeal.ogg',
    preparationOrderCancel: 'preparationOrderCancel.ogg',
    superBigOrderDeliveryCancel: 'superBigOrderDeliveryCancel.ogg',
    etaChangeRequest: 'etaChangeRequest.ogg',
    dispatcherChangeSuccess: 'dispatcherChangeSuccess.ogg',
    dispatcherChangeFail: 'dispatcherChangeFail.ogg',
    preOrderETAChangeSound: 'preOrderETAChangeSound.ogg',
    preOrderAutoPartialRefund: 'preOrderPartialRefund.ogg',
    /** 补送状态 */
    busongCancel: 'busongCancel.ogg',
    negativeOrder: 'negativeOrder.ogg',
    /** 商家核实无餐异常，商品卖空 */
    dispatcherSellOut: 'dispatcherSellOut.ogg',
    /** 商家核实无餐异常，自取 */
    dispatchSelfPick: 'dispatchSelfPick.ogg',
    /** 商家核实无餐异常，自行配送 */
    dispatcherZiPei: 'dispatcherZiPei.ogg',
} as const;

type BellKey = keyof typeof bellResourceMap;

export const resourceUrlPrefix = [ENV.LOCALHOST, ENV.DEVELOPMENT, ENV.TEST, ENV.STAGE].includes(
    getEnv(),
)
    ? '//s3plus.sankuai.com'
    : '';

/** 提取铃声音频文件资源路径 */
const getResourceUrl = (key: BellKey) => {
    return `${resourceUrlPrefix}/v1/mss_0e4451a82e1d428f9fc3b99c0b3ebf15/waimaie/audio/v1/${bellResourceMap[key]}`;
};

/** 部分内置铃声的默认播放次数 */
const defaultBellLoopTimesMap: Partial<Record<BellKey, number>> = {
    preorder: 3,
    preorderAcceptedByRider: 3,
    neworder: 3,
    autoneworder: 1,
    other: 1,
    reminder: 1,
    cancel: 3,
    refund: 3,
    refundUrge: 1,
    exception: 1,
    autoSendDeliveryFail: 1,
};

/** 提取各个铃声的播放次数 */
const getBellLoopTimes = (key: BellKey): number => {
    const localConfigStr = getStorage(`waimai_e_order_voice_times_${key}`);
    const localConfigTimes = Number(localConfigStr);
    if (
        !Number.isNaN(localConfigTimes) &&
        localConfigTimes >= 0 &&
        localConfigStr !== null &&
        localConfigStr !== ''
    ) {
        return localConfigTimes;
    } else {
        return defaultBellLoopTimesMap[key] || 1;
    }
};

const localVolumeStr = getStorage('waimai_e_order_voice_volume');
let volume = 1;
if (localVolumeStr !== null) {
    const localVolume = Number(localVolumeStr);
    if (!Number.isNaN(localVolume)) {
        volume = Math.max(0, Math.min(1, localVolume));
    }
}

/** 商家中心内置铃声 */
const configList: BellConstructorConfig[] = [
    {
        key: 'orderWillTimeout',
        resourceUrl: getResourceUrl('orderWillTimeout'),
        priority: 5,
        desc: '防漏单提醒',
        loopTimes: getBellLoopTimes('orderWillTimeout'),
        volume,
    },
    {
        key: 'cancel',
        resourceUrl: getResourceUrl('cancel'),
        priority: 10,
        desc: '订单取消',
        loopTimes: getBellLoopTimes('cancel'),
        volume,
    },
    // 在不同场景下播放的次数不一样，已有的铃声次数设置不适用；单独作为一个次数固定的铃声。
    {
        key: 'preorder-partial-refund/1',
        resourceUrl: getResourceUrl('preOrderAutoPartialRefund'),
        priority: 15,
        desc: '预订单自动部分退款（1遍）',
        loopTimes: 1,
        volume,
    },
    {
        key: 'preorder-partial-refund/3',
        resourceUrl: getResourceUrl('preOrderAutoPartialRefund'),
        priority: 15,
        desc: '预订单自动部分退款（3遍）',
        loopTimes: 3,
        volume,
    },
    {
        key: 'refund',
        resourceUrl: getResourceUrl('refund'),
        priority: 20,
        desc: '退款',
        loopTimes: getBellLoopTimes('refund'),
        volume,
    },
    {
        key: 'negativeIncome',
        resourceUrl: getResourceUrl('negativeIncome'),
        priority: 20,
        desc: '预计收入为负',
        loopTimes: getBellLoopTimes('negativeIncome'),
        volume,
    },
    {
        key: 'refundUrge',
        resourceUrl: getResourceUrl('refundUrge'),
        priority: 21,
        desc: '催退款审核',
        loopTimes: getBellLoopTimes('refundUrge'),
        volume,
    },
    {
        key: 'autoAcceptDisconnect',
        resourceUrl: getResourceUrl('autoAcceptDisconnect'),
        priority: 25,
        desc: '门店自动接单断开',
        loopTimes: getBellLoopTimes('autoAcceptDisconnect'),
        volume,
    },

    {
        key: 'preparationOrderCancel',
        resourceUrl: getResourceUrl('preparationOrderCancel'),
        priority: 27,
        desc: '预售单-取消',
        loopTimes: getBellLoopTimes('preparationOrderCancel'),
        volume,
    },
    {
        key: 'preparationOrderNew',
        resourceUrl: getResourceUrl('preparationOrderNew'),
        priority: 28,
        desc: '预售单-新订单',
        loopTimes: getBellLoopTimes('preparationOrderNew'),
        volume,
    },

    {
        key: 'mealSlow_sharkOrder',
        resourceUrl: getResourceUrl('mealSlow_sharkOrder'),
        priority: 30,
        desc: '出餐慢（短时间大量进单）',
        loopTimes: getBellLoopTimes('mealSlow_sharkOrder'),
        volume,
    },
    {
        key: 'mealSlow_riderWait',
        resourceUrl: getResourceUrl('mealSlow_riderWait'),
        priority: 31,
        desc: '出餐慢（骑手等待）',
        loopTimes: getBellLoopTimes('mealSlow_riderWait'),
        volume,
    },
    {
        key: 'mealSlow_other',
        resourceUrl: getResourceUrl('mealSlow_other'),
        priority: 32,
        desc: '出餐慢（其他场景）',
        loopTimes: getBellLoopTimes('mealSlow_other'),
        volume,
    },
    {
        key: 'mealSlow_multiSku',
        resourceUrl: getResourceUrl('mealSlow_multiSku'),
        priority: 33,
        desc: '出餐慢（订单内多SKU）',
        loopTimes: getBellLoopTimes('mealSlow_multiSku'),
        volume,
    },
    {
        key: 'mealSlow_bigOrder',
        resourceUrl: getResourceUrl('mealSlow_bigOrder'),
        priority: 34,
        desc: '出餐慢（大额订单）',
        loopTimes: getBellLoopTimes('mealSlow_bigOrder'),
        volume,
    },
    {
        key: 'meal-exception',
        resourceUrl: getResourceUrl('meal-exception'),
        priority: 40,
        desc: '出餐异常',
        loopTimes: getBellLoopTimes('meal-exception'),
        volume,
    },
    {
        key: 'meal-slow',
        resourceUrl: getResourceUrl('meal-slow'),
        priority: 50,
        desc: '出餐慢',
        loopTimes: getBellLoopTimes('meal-slow'),
        volume,
    },
    {
        key: 'autoSendDeliveryFail',
        resourceUrl: getResourceUrl('autoSendDeliveryFail'),
        priority: 60,
        desc: '自动发配送失败',
        loopTimes: getBellLoopTimes('autoSendDeliveryFail'),
        volume,
    },
    {
        key: 'other',
        resourceUrl: getResourceUrl('other'),
        priority: 75,
        desc: '其他设备接单',
        loopTimes: getBellLoopTimes('other'),
        volume,
    },
    {
        key: 'preorderOther',
        resourceUrl: getResourceUrl('preorderOther'),
        priority: 75,
        desc: '预订单被其他设备接单',
        loopTimes: getBellLoopTimes('other'), // 和 other 共用同一个循环次数配置
        volume,
    },
    {
        key: 'autoneworder',
        resourceUrl: getResourceUrl('autoneworder'),
        priority: 80,
        desc: '自动接单',
        loopTimes: getBellLoopTimes('autoneworder'),
        volume,
        interruptable: false,
    },
    {
        key: 'autonewpreorder',
        resourceUrl: getResourceUrl('autonewpreorder'),
        priority: 80,
        desc: '（预订单）自动接单',
        loopTimes: getBellLoopTimes('autoneworder'), // 和 autoneworder 共用同一个循环次数配置
        volume,
        interruptable: false,
    },
    {
        key: 'yxsNewOrder',
        resourceUrl: getResourceUrl('yxsNewOrder'),
        priority: 80,
        desc: '自动接单(优享送)',
        loopTimes: getBellLoopTimes('autoneworder'), // 优享送铃声复用商家自动接单铃声播放次数
        volume,
        interruptable: false,
    },
    {
        key: 'negativeOrder',
        resourceUrl: getResourceUrl('negativeOrder'),
        priority: 85,
        desc: '负订单',
        volume,
    },
    {
        key: 'neworder',
        resourceUrl: getResourceUrl('neworder'),
        priority: 90,
        desc: '新订单',
        loopTimes: getBellLoopTimes('neworder'),
        volume,
    },
    {
        key: 'newpreorder',
        resourceUrl: getResourceUrl('newpreorder'),
        priority: 90,
        desc: '（预订单）新订单',
        loopTimes: getBellLoopTimes('neworder'), // 和 neworder 共用同一个循环次数配置
        volume,
    },
    {
        key: 'etaChangeRequest',
        resourceUrl: getResourceUrl('etaChangeRequest'),
        priority: 95,
        desc: 'ETA 变更申请',
        loopTimes: getBellLoopTimes('etaChangeRequest'),
        volume,
    },
    {
        key: 'preOrderETAChangeSound',
        resourceUrl: getResourceUrl('preOrderETAChangeSound'),
        priority: 95,
        desc: 'ETA 变更申请(自动通过)',
        loopTimes: getBellLoopTimes('preOrderETAChangeSound'),
        volume,
    },
    {
        key: 'preorderAcceptedByRider',
        resourceUrl: getResourceUrl('preorderAcceptedByRider'),
        priority: 99,
        desc: '预订单被骑手接单',
        loopTimes: getBellLoopTimes('preorderAcceptedByRider'),
        volume,
    },
    {
        key: 'preorder',
        resourceUrl: getResourceUrl('preorder'),
        priority: 100,
        desc: '预订单',
        loopTimes: getBellLoopTimes('preorder'),
        volume,
    },
    {
        key: 'dispatcherChangeSuccess',
        resourceUrl: getResourceUrl('dispatcherChangeSuccess'),
        priority: 102,
        desc: '换骑手-成功',
        loopTimes: getBellLoopTimes('dispatcherChangeSuccess'),
        volume,
    },
    {
        key: 'dispatcherChangeFail',
        resourceUrl: getResourceUrl('dispatcherChangeFail'),
        priority: 102,
        desc: '换骑手-失败',
        loopTimes: getBellLoopTimes('dispatcherChangeFail'),
        volume,
    },
    {
        key: 'preparationOrderPrepMeal',
        resourceUrl: getResourceUrl('preparationOrderPrepMeal'),
        priority: 103,
        desc: '预售单-待出餐',
        loopTimes: getBellLoopTimes('preparationOrderPrepMeal'),
        volume,
    },
    {
        key: 'superBigOrderDeliveryCancel',
        resourceUrl: getResourceUrl('superBigOrderDeliveryCancel'),
        priority: 104,
        desc: '配送异常-超大订单配送取消',
        loopTimes: getBellLoopTimes('superBigOrderDeliveryCancel'),
        volume,
    },
    {
        key: 'noDispatcherAcceptOrder',
        resourceUrl: getResourceUrl('noDispatcherAcceptOrder'),
        priority: 105,
        desc: '配送异常-长时间无骑手接单',
        loopTimes: getBellLoopTimes('noDispatcherAcceptOrder'),
        volume,
    },
    {
        key: 'dispatcherNotTakeMeal',
        resourceUrl: getResourceUrl('dispatcherNotTakeMeal'),
        priority: 106,
        desc: '配送异常-骑手长时间不取餐',
        loopTimes: getBellLoopTimes('dispatcherNotTakeMeal'),
        volume,
    },
    {
        key: 'dispatcherNotArrival',
        resourceUrl: getResourceUrl('dispatcherNotArrival'),
        priority: 107,
        desc: '配送异常-骑手长时间不到店',
        loopTimes: getBellLoopTimes('dispatcherNotArrival'),
        volume,
    },
    {
        key: 'dispatcherCancel',
        resourceUrl: getResourceUrl('dispatcherCancel'),
        priority: 108,
        desc: '配送异常-跑腿骑手取消',
        loopTimes: getBellLoopTimes('dispatcherCancel'),
        volume,
    },
    {
        key: 'dispatchStopped',
        resourceUrl: getResourceUrl('dispatchStopped'),
        priority: 108.1,
        desc: '系统停止调度',
        loopTimes: getBellLoopTimes('dispatchStopped'),
        volume,
    },
    {
        key: 'dispatchPaused',
        resourceUrl: getResourceUrl('dispatchPaused'),
        priority: 108.2,
        desc: '系统暂停调度',
        loopTimes: getBellLoopTimes('dispatchPaused'),
        volume,
    },
    {
        key: 'dispatcherCancelAndRedispatch',
        resourceUrl: getResourceUrl('dispatcherCancelAndRedispatch'),
        priority: 108.4,
        desc: '骑手取消 & 自动重发配送',
        loopTimes: getBellLoopTimes('dispatcherCancelAndRedispatch'),
        volume,
    },
    {
        key: 'dispatcherTransfer',
        resourceUrl: getResourceUrl('dispatcherTransfer'),
        priority: 109,
        desc: '配送异常-跑腿骑手转单',
        loopTimes: getBellLoopTimes('dispatcherTransfer'),
        volume,
    },
    {
        key: 'exception',
        resourceUrl: getResourceUrl('exception'),
        priority: 110,
        desc: '订单配送异常',
        loopTimes: getBellLoopTimes('exception'),
        volume,
    },
    {
        key: 'dispatcherSellOut',
        resourceUrl: getResourceUrl('dispatcherSellOut'),
        priority: 110,
        desc: '商家核实无餐异常，商品卖空',
        loopTimes: getBellLoopTimes('dispatcherSellOut'),
        volume,
    },
    {
        key: 'dispatchSelfPick',
        resourceUrl: getResourceUrl('dispatchSelfPick'),
        priority: 110,
        desc: '商家核实无餐异常，自取',
        loopTimes: getBellLoopTimes('dispatchSelfPick'),
        volume,
    },
    {
        key: 'dispatcherZiPei',
        resourceUrl: getResourceUrl('dispatcherZiPei'),
        priority: 110,
        desc: '商家核实无餐异常，自行配送',
        loopTimes: getBellLoopTimes('dispatcherZiPei'),
        volume,
    },
    {
        key: 'paotui-exception',
        resourceUrl: getResourceUrl('paotui-exception'),
        priority: 115,
        desc: '订单配送异常(跑腿)',
        loopTimes: getBellLoopTimes('paotui-exception'),
        volume,
    },
    {
        key: 'newDispatcher',
        resourceUrl: getResourceUrl('newDispatcher'),
        priority: 116,
        desc: '新的骑手接单',
        loopTimes: getBellLoopTimes('newDispatcher'),
        volume,
    },

    {
        key: 'message-remind1',
        resourceUrl: getResourceUrl('message-remind1'),
        priority: 120,
        desc: 'IM @商家消息提醒',
        loopTimes: getBellLoopTimes('message-remind1'),
        volume,
    },
    {
        key: 'new-message',
        resourceUrl: getResourceUrl('new-message'),
        priority: 150,
        desc: 'IM 新消息提醒',
        loopTimes: getBellLoopTimes('new-message'),
        volume,
    },
    {
        key: 'reminder',
        resourceUrl: getResourceUrl('reminder'),
        priority: 160,
        desc: '催单',
        loopTimes: getBellLoopTimes('reminder'),
        volume,
    },
    {
        key: 'breakRule',
        resourceUrl: getResourceUrl('breakRule'),
        priority: 170,
        desc: '规则中心-违规通知',
        loopTimes: getBellLoopTimes('breakRule'),
        volume,
    },
    {
        key: 'earlyWarning',
        resourceUrl: getResourceUrl('earlyWarning'),
        priority: 180,
        desc: '规则中心-违规预警',
        loopTimes: getBellLoopTimes('earlyWarning'),
        volume,
    },
    {
        key: 'busongCancel',
        resourceUrl: getResourceUrl('busongCancel'),
        priority: 190,
        desc: '跑腿补送已取消',
        loopTimes: getBellLoopTimes('busongCancel'),
        volume,
    },
];
export { bellResourceMap, getBellLoopTimes, defaultBellLoopTimesMap };
export default configList;
